import { addDays, compareLocalDate, type LocalDate } from '../local-date.js';
import {
  completionRate,
  DRILL_UPCOMING_DAYS,
  type DrillCycle,
  type DrillPlanState,
  type DrillStatus,
} from './engine.js';

/**
 * Tatbikat panosu ve süzgeçleri (belge §2 "Dashboard"; ADR 0128 Faz 2 Ek). Saf: gemi uygulaması,
 * web ofis ve zil aynı hesabı kullanır.
 *
 * - **Planned**: dönemde vadesi olan döngü (taslak ve iptal planlar sayılmaz).
 * - **Completed**: dönemde yapılmış, kaptanın onayladığı uygulama.
 * - **Upcoming**: bugünden itibaren 30 gün içinde vadesi gelen, gecikmemiş plan.
 * - **Overdue / Postponed**: bugünkü durum; dönemle sınırlanmaz (ADR 0108 Ek).
 * - **Completion rate**: vadesi dönemde olan sonuçlu döngülerde vaktinde yapılanın payı.
 */

export interface DrillDashboardPlan {
  vesselId: string;
  templateId: string;
  categoryId: string;
  responsibleUserId: string | null;
  state: DrillPlanState;
  status: DrillStatus;
  nextDueOn: LocalDate;
  /** Ertelenmemiş vade [NR496 4.5.3d]. */
  originalDueOn: LocalDate;
  /** Onaylı uygulamaların günleri. */
  conducts: readonly LocalDate[];
  cycles: readonly DrillCycle[];
}

export interface DrillKpis {
  planned: number;
  completed: number;
  upcoming: number;
  overdue: number;
  postponed: number;
  completionRate: { onTime: number; counted: number; percent: number | null };
}

const inRange = (date: LocalDate, from: LocalDate, to: LocalDate) =>
  compareLocalDate(date, from) >= 0 && compareLocalDate(date, to) <= 0;

export function drillKpis(
  plans: readonly DrillDashboardPlan[],
  range: { from: LocalDate; to: LocalDate; today: LocalDate },
): DrillKpis {
  const counted = plans.filter((p) => p.state === 'active');
  const soon = addDays(range.today, DRILL_UPCOMING_DAYS);
  const cycles = counted.flatMap((p) => p.cycles);
  return {
    planned: cycles.filter((c) => inRange(c.dueOn, range.from, range.to)).length,
    completed: plans
      .filter((p) => p.state !== 'draft')
      .flatMap((p) => p.conducts)
      .filter((d) => inRange(d, range.from, range.to)).length,
    upcoming: counted.filter(
      (p) => p.status !== 'overdue' && inRange(p.nextDueOn, range.today, soon),
    ).length,
    overdue: counted.filter((p) => p.status === 'overdue').length,
    postponed: counted.filter((p) => p.status === 'postponed').length,
    completionRate: completionRate(cycles, range),
  };
}

export interface DrillPlanFilter {
  from?: LocalDate | null;
  to?: LocalDate | null;
  /** Boş: bütün durumlar. Durumlar kendi aralarında "ya da". */
  statuses?: readonly DrillStatus[];
  vesselIds?: readonly string[];
  templateIds?: readonly string[];
  categoryIds?: readonly string[];
  responsibleUserIds?: readonly string[];
}

/** Dönemle sınırlanmayan durumlar (ADR 0108 Ek): gecikmiş ve ertelenmiş her dönemde görünür. */
const UNBOUNDED: readonly DrillStatus[] = ['overdue', 'postponed'];

const allows = (list: readonly string[] | undefined, value: string | null) =>
  list === undefined || list.length === 0 || (value !== null && list.includes(value));

/**
 * Süzgeç (ADR 0108 Ek kalıbı): nitelikler (gemi, tür, kategori, sorumlu) "ve"; durumlar "ya da".
 * Tarih aralığı verilmişse plan vadesi ya da bir onaylı uygulaması aralıkta olmalı; gecikmiş ve
 * ertelenmiş plan aralığa bakmaz. Takvim aynı süzgeci kullanır.
 */
export function drillPlanMatches(plan: DrillDashboardPlan, filter: DrillPlanFilter): boolean {
  if (!allows(filter.vesselIds, plan.vesselId)) return false;
  if (!allows(filter.templateIds, plan.templateId)) return false;
  if (!allows(filter.categoryIds, plan.categoryId)) return false;
  if (!allows(filter.responsibleUserIds, plan.responsibleUserId)) return false;
  if (filter.statuses && filter.statuses.length > 0 && !filter.statuses.includes(plan.status)) {
    return false;
  }
  if (UNBOUNDED.includes(plan.status)) return true;
  const from = filter.from ?? null;
  const to = filter.to ?? null;
  if (from === null && to === null) return true;
  const lo = from ?? '0000-01-01';
  const hi = to ?? '9999-12-31';
  return (
    inRange(plan.nextDueOn, lo, hi) ||
    plan.conducts.some((d) => inRange(d, lo, hi)) ||
    plan.cycles.some((c) => inRange(c.dueOn, lo, hi))
  );
}
